import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useWoody } from '../../woody/woody.js';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import pLimit from 'p-limit';
import { useQueryCacheResourceHelpers } from '../utils/useQueryCacheResourceHelpers.js';
import plur from 'plur';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { toast } from '../../store/alerts.js';
import { withConfirmation } from '../../queries/utils/withConfirmation.js';
import { resourceQueryPredicates } from '../queries/resourceQueryPredicates.js';
import { dispatchFabricEvent } from '../../hooks/useFabricEvent.js';
import { removeItemsWithProvidedIdFromHistory } from '../../app/stores/userNavigationHistoryStore.js';

const useMutationDeleteLeaveResources = () => {
  const { client } = useWoody();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const { deleteCachedResources } = useQueryCacheResourceHelpers();
  const accessToken = useContextAccessToken();
  const { data: user } = useQueryUserInfo();
  const mutation = useMutation({
    mutationFn: async (variables) => {
      if (variables.resourceIds && variables.resourceIds.length > 0) {
        await client.v2("/v2/resource/delete", {
          method: "post",
          body: {
            resourceIds: variables.resourceIds
          },
          query: {
            accessToken
          }
        });
      }
      if (variables.resourceIdsToLeave && variables.resourceIdsToLeave.length > 0 && user?.id) {
        const limit = pLimit(3);
        await Promise.all(variables.resourceIdsToLeave.map((resourceId) => limit(() => client.v2({
          endpoint: "/v2/resources/{resourceId}/permissions/{userId}",
          params: {
            resourceId,
            userId: user.id
          }
        }, {
          method: "delete"
        }))));
      }
      return {
        resourceIds: [
          ...variables.resourceIds || [],
          ...variables.resourceIdsToLeave || []
        ]
      };
    },
    onMutate: (variables) => {
      return deleteCachedResources([
        ...variables.resourceIds || [],
        ...variables.resourceIdsToLeave || []
      ]);
    },
    onSuccess: (_res, variables, ctx) => {
      const deletedItemsTotal = variables.resourceIds?.length || 0;
      const leftItemsTotal = variables.resourceIdsToLeave?.length || 0;
      removeItemsWithProvidedIdFromHistory([
        ...variables.resourceIds || [],
        ...variables.resourceIdsToLeave || []
      ]);
      if (deletedItemsTotal && !variables.noToast) {
        toast({
          content: deletedItemsTotal > 1 ? `${deletedItemsTotal} items deleted` : "Item deleted"
        });
        track(AnalyticsEvents.DeletedResource, {
          count: deletedItemsTotal
        });
        dispatchFabricEvent("fabric-resources-deleted", {
          quantity: deletedItemsTotal
        });
      }
      if (leftItemsTotal) {
        toast({
          content: leftItemsTotal > 1 ? `${leftItemsTotal} items left` : "Item left"
        });
      }
      queryClient.invalidateQueries({
        predicate: resourceQueryPredicates.summaryAll
      });
      ctx?.optimisticallyUpdateDirectQueries.invalidateQueries();
    },
    onError: (_, variables, context) => {
      if (!variables.noToast) {
        toast({
          content: "Failed to delete"
        });
      }
      context?.resetCacheToPreOptimisticState();
      context?.invalidateQueries();
    }
  });
  return withConfirmation(mutation, (variables) => {
    const titleLabel = variables.resourceIdsToLeave && variables.resourceIdsToLeave.length > 0 && variables.resourceIds && variables.resourceIds.length > 0 ? "Delete/leave" : variables.resourceIdsToLeave && variables.resourceIdsToLeave.length > 0 ? "Leave" : "Delete";
    const totalItemsCount = (variables.resourceIds || []).length + (variables.resourceIdsToLeave || []).length;
    return {
      title: `${titleLabel} ${plur("item", totalItemsCount)} - Are you sure?`,
      content: `Are you sure you want to ${titleLabel.toLowerCase()} ${plur("this", "these", totalItemsCount)} ${plur("item", totalItemsCount)}? This action cannot be undone.`,
      confirmLabel: `Yes, ${titleLabel}`,
      cancelLabel: "Cancel",
      confirmButtonProps: {
        variant: "danger"
      }
    };
  });
};

export { useMutationDeleteLeaveResources };
//# sourceMappingURL=useMutationDeleteLeaveResources.js.map
